import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import fallbackLogo from "./assets/logo.jpeg";
import { useFacility, splitFacilityName } from "./context/FacilityContext";
import { ASSETS } from "./BaseURL";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { facility } = useFacility();
  const [firstWord, restName] = splitFacilityName(facility?.name);
  const logoSrc = facility?.logo ? `${ASSETS}${facility.logo}` : fallbackLogo;
  const altText = facility?.name || "Hotel";

  // Visitor session for the public reservation flow.  Re-read on every
  // render so login / logout reflects without a page reload.
  let guest = null;
  try { guest = JSON.parse(localStorage.getItem("guest") || "null"); } catch (_) {}

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Home",        to: "/" },
    { label: "Reserve",     to: "/reserve" },
    { label: "Locations",   to: "/locations" },
    { label: "Contact Us",  to: "/contact" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=Jost:wght@200;300;400;500&display=swap');

        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: background 0.4s ease, box-shadow 0.4s ease, padding 0.4s ease;
          font-family: 'Jost', sans-serif;
        }

        .navbar.transparent {
          background: linear-gradient(
            to bottom,
            rgba(15, 8, 3, 0.72) 0%,
            rgba(15, 8, 3, 0.0) 100%
          );
          box-shadow: none;
        }

        .navbar.scrolled {
          background: rgba(22, 13, 6, 0.97);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow: 0 1px 0 rgba(200, 160, 90, 0.12), 0 4px 32px rgba(0,0,0,0.45);
        }

        .navbar-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 48px;
          height: 82px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: height 0.4s ease;
        }

        .navbar.scrolled .navbar-inner {
          height: 68px;
        }

        /* ── LOGO ── */
        .nav-logo-wrap {
          display: flex;
          align-items: center;
          gap: 14px;
          text-decoration: none;
          flex-shrink: 0;
        }

        .nav-logo-img-ring {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .nav-logo-img-ring::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          border: 1.5px solid rgba(200, 160, 90, 0.45);
          transition: border-color 0.3s;
        }

        .nav-logo-wrap:hover .nav-logo-img-ring::before {
          border-color: rgba(200, 160, 90, 0.85);
        }

        .nav-logo-img {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          object-fit: cover;
          display: block;
          transition: transform 0.4s ease;
        }

        .navbar.scrolled .nav-logo-img {
          width: 52px;
          height: 52px;
        }

        .nav-logo-wrap:hover .nav-logo-img {
          transform: scale(1.04);
        }

        .nav-logo-text-block {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .nav-logo-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 20px;
          font-weight: 400;
          color: #f0e4cf;
          letter-spacing: 0.06em;
          line-height: 1;
        }

        .nav-logo-name em {
          font-style: italic;
          color: #c8a05a;
        }

        .nav-logo-tagline {
          font-size: 8.5px;
          font-weight: 300;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(200, 160, 90, 0.5);
          line-height: 1;
        }

        /* ── LINKS ── */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .nav-link {
          position: relative;
          padding: 8px 14px;
          font-size: 10.5px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(240, 228, 207, 0.6);
          text-decoration: none;
          transition: color 0.2s;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 14px;
          right: 14px;
          height: 1px;
          background: #c8a05a;
          transform: scaleX(0);
          transform-origin: center;
          transition: transform 0.3s cubic-bezier(.22,1,.36,1);
        }

        .nav-link:hover,
        .nav-link.active {
          color: rgba(240, 228, 207, 0.95);
        }

        .nav-link:hover::after,
        .nav-link.active::after {
          transform: scaleX(1);
        }

        /* ── SEPARATOR ── */
        .nav-sep {
          width: 1px;
          height: 20px;
          background: rgba(200, 160, 90, 0.2);
          margin: 0 10px;
        }

        /* ── CTA BUTTON ── */
        .nav-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 24px;
          background: transparent;
          border: 1.5px solid rgba(200, 160, 90, 0.55);
          border-radius: 3px;
          font-family: 'Jost', sans-serif;
          font-size: 10.5px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c8a05a;
          text-decoration: none;
          position: relative;
          overflow: hidden;
          transition: color 0.3s, border-color 0.3s;
        }

        .nav-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #c8a05a;
          transform: translateX(-101%);
          transition: transform 0.35s cubic-bezier(.22,1,.36,1);
        }

        .nav-cta.primary {
          background: #c8a05a;
          color: #1a110a;
          border-color: #c8a05a;
        }
        .nav-cta.primary::before { background: #d4ae6a; }

        .nav-cta:hover {
          color: #1a110a;
          border-color: #c8a05a;
        }

        /* Smaller chip used for the management / guest profile links. */
        .nav-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 12px;
          background: transparent;
          border: 1px solid rgba(200,160,90,.25);
          border-radius: 3px;
          font-size: 9.5px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(240,228,207,.55);
          text-decoration: none;
          transition: color .2s, border-color .2s;
        }
        .nav-chip:hover { color: #c8a05a; border-color: rgba(200,160,90,.5); }

        .nav-cta:hover::before {
          transform: translateX(0);
        }

        .nav-cta span,
        .nav-cta svg {
          position: relative;
          z-index: 1;
        }

        /* ── MOBILE TOGGLE ── */
        .nav-hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
        }

        .nav-hamburger span {
          display: block;
          width: 24px;
          height: 1.5px;
          background: #f0e4cf;
          border-radius: 2px;
          transition: transform 0.3s, opacity 0.3s, width 0.3s;
        }

        .nav-hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
        .nav-hamburger.open span:nth-child(2) { opacity: 0; width: 0; }
        .nav-hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

        /* ── MOBILE MENU ── */
        .nav-mobile-menu {
          display: none;
          position: fixed;
          inset: 0;
          z-index: 999;
          background: rgba(20, 10, 5, 0.98);
          backdrop-filter: blur(12px);
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          animation: menuFadeIn 0.3s ease both;
        }

        .nav-mobile-menu.open {
          display: flex;
        }

        .mobile-link {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          font-weight: 300;
          color: rgba(240, 228, 207, 0.75);
          text-decoration: none;
          letter-spacing: 0.03em;
          transition: color 0.2s;
          padding: 6px 0;
        }

        .mobile-link:hover { color: #c8a05a; }

        .mobile-cta {
          margin-top: 24px;
          padding: 14px 40px;
          border: 1.5px solid rgba(200, 160, 90, 0.5);
          border-radius: 3px;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c8a05a;
          text-decoration: none;
        }

        .mobile-close {
          position: absolute;
          top: 28px;
          right: 28px;
          background: none;
          border: none;
          color: rgba(240,228,207,.6);
          cursor: pointer;
          padding: 6px;
        }

        @keyframes menuFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @media (max-width: 900px) {
          .nav-links   { display: none; }
          .nav-hamburger { display: flex; }
          .navbar-inner { padding: 0 24px; }
        }
      `}</style>

      {/* Mobile overlay menu */}
      <div className={`nav-mobile-menu ${menuOpen ? "open" : ""}`}>
        <button className="mobile-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        {links.map((l) => (
          <Link key={l.label} to={l.to} className="mobile-link" onClick={() => setMenuOpen(false)}>
            {l.label}
          </Link>
        ))}
        <Link to="/reserve" className="mobile-cta" onClick={() => setMenuOpen(false)}
              style={{ background:"#c8a05a", color:"#1a110a", borderColor:"#c8a05a" }}>
          Reserve a Room
        </Link>
        {guest ? (
          <Link to="/my-reservations" className="mobile-cta" onClick={() => setMenuOpen(false)}>
            My Reservations
          </Link>
        ) : (
          <Link to="/guest/login" className="mobile-cta" onClick={() => setMenuOpen(false)}>
            Guest Sign In
          </Link>
        )}
        <Link to="/login" className="mobile-cta" onClick={() => setMenuOpen(false)}
              style={{ fontSize: 10 }}>
          Staff Portal
        </Link>
      </div>

      {/* Main Navbar */}
      <header className={`navbar ${scrolled ? "scrolled" : "transparent"}`}>
        <div className="navbar-inner">

          {/* Logo */}
          <Link to="/" className="nav-logo-wrap">
            <div className="nav-logo-img-ring">
              <img src={logoSrc} alt={altText} className="nav-logo-img" />
            </div>
            <div className="nav-logo-text-block">
              <span className="nav-logo-name">
                {firstWord ? <em>{firstWord}</em> : null}
                {restName ? ` ${restName}` : null}
                {!firstWord && !restName ? "Hotel" : null}
              </span>
              <span className="nav-logo-tagline">
                {facility?.location || "Premium Hospitality"}
              </span>
            </div>
          </Link>

          {/* Desktop Links */}
          <nav className="nav-links">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`nav-link ${location.pathname === l.to ? "active" : ""}`}
              >
                {l.label}
              </Link>
            ))}

            <div className="nav-sep" />

            {/* Primary visitor CTA — direct booking flow. */}
            <Link to="/reserve" className="nav-cta primary">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7h18v13H3z"/><path d="M3 12h18"/>
              </svg>
              <span>Reserve a Room</span>
            </Link>

            {/* Either the signed-in guest's name (→ my reservations) or
                a small sign-in link, plus the staff portal entrance. */}
            {guest ? (
              <Link to="/my-reservations" className="nav-chip" title={guest.email}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
                {guest.full_name?.split(" ")[0] || "My Stays"}
              </Link>
            ) : (
              <Link to="/guest/login" className="nav-chip">Sign in</Link>
            )}

            <Link to="/login" className="nav-chip">Staff</Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className={`nav-hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>
    </>
  );
}